<template>
  <avs-card class="pb-4">
    <template v-slot:title>
      <v-toolbar
        class="mt-4 custom-toolbar"
        elevation="0"
      >
        <v-text-field
          class="mr-8"
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
        />

        <v-select
          hide-details
          dense
          outlined
          v-model="archivedFilter"
          :items="archivedFilterItems"
          prepend-inner-icon="mdi-filter-variant"
          class="mr-4"
          style="max-width: 200px"
        />

        <avs-createButton
          :spantext="$t('Ajouter un nouveau parametre')"
          menu
          :name="$t('Nouveau parametre')"
          @click="handleOpenEditDialog()"
        >
        </avs-createButton>
      </v-toolbar>
    </template>
    <template v-slot:content>
      <v-data-table
        class="ma-10"
        dense
        :headers="headers"
        :no-data-text="$t('messages.information.no_data')"
        :items="filteredItems"
        :items-per-page="-1"
        :search="search"
        item-key="id"
        @dblclick:row="
          ($event, { item }) => {
            if (allow.duplicate(item)) {
              handleOpenEditDialog(item)
            }
          }
        "
      >
        <template v-slot:item.name="{ item }">
          {{ item.name }}
          <v-chip
            v-if="item.archived"
            x-small
            color="grey"
            text-color="white"
            class="ml-2"
          >
            {{ $t("Archivé") }}
          </v-chip>
        </template>
        <template v-slot:item.price="{ item }"> {{ formatNumber(item.price) }} FCFA </template>
        <template v-slot:item.created_at="{ item }">
          {{ toDatetime(item.created_at) }}
        </template>
        <!-- <template v-slot:item.status="{ item }">
          <avs-chip :label="false">
            {{ item.status }}
          </avs-chip>
        </template> -->
        <template v-slot:item.status="{ item }">
          <component :is="handleStatusComponent(item)" />
        </template>
        <template v-slot:item.checked="{ item }">
          <div class="d-flex justify-center">
            <avs-button
              icon
              color="secondary"
              :iconI="'mdi-checkbox-multiple-outline'"
              label
              fab
              small
              @click.stop="handleOpenConfirmDialog(item)"
              :spantext="
                $t('actions.open', {
                  entity: $t('commons.drawer.shop').toLowerCase()
                })
              "
            />
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center">
            <avs-editButton
              @click.stop="handleOpenEditDialog(item, false)"
              :disabled="isDisabled(item)"
              :spantext="
                $t('actions.edit', {
                  entity: $t('commons.drawer.shop').toLowerCase()
                })
              "
            />
            <avs-button
              icon
              color="secondary"
              :iconI="'mdi-eye'"
              label
              fab
              small
              @click.stop="handleOpenEditDialog(item)"
              :spantext="
                $t('actions.open', {
                  entity: $t('commons.drawer.shop').toLowerCase()
                })
              "
            />

            <avs-deleteButton
              @click.stop="handleOpenDeleteDialog(item)"
              :disabled="isDisabled(item)"
              :spantext="$t('actions.delete', { entity: $t('commons.fieldtype').toLowerCase() })"
            />

            <avs-button
              icon
              :color="item.archived ? 'success' : 'warning'"
              :iconI="
                item.archived ? 'mdi-archive-arrow-up-outline' : 'mdi-archive-arrow-down-outline'
              "
              label
              fab
              small
              @click.stop="handleArchive(item)"
              :spantext="item.archived ? $t('Désarchiver') : $t('Archiver')"
            />
          </div>
        </template>
      </v-data-table>
      <!-- v-html="document.description" -->

      <v-dialog
        v-model="confirmDialogIsOpen"
        width="40vw"
      >
        <v-card>
          <v-card-title class="headline d-flex justify-center">
            {{ $t("messages.information.title") }}
          </v-card-title>
          <v-card-text class="text-center">
            <p>
              {{
                $t("messages.information.confirmation", {
                  entity: selectedItem?.name
                })
              }}
            </p>
            <v-icon
              large
              class="my-4"
              color="primary"
              >mdi-help-circle-outline</v-icon
            >
          </v-card-text>
          <v-card-actions class="d-flex justify-center">
            <v-btn
              color="success"
              @click="confirmAction('accept')"
            >
              <v-icon left>mdi-check</v-icon>
              {{ $t("commons.accept") }}
            </v-btn>
            <v-btn
              color="error"
              @click="confirmAction('reject')"
            >
              <v-icon left>mdi-close</v-icon>
              {{ $t("commons.reject") }}
            </v-btn>
            <v-btn
              color="secondary"
              @click="confirmAction('close')"
            >
              <v-icon left>mdi-close-circle-outline</v-icon>
              {{ $t("commons.close") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="editDialogIsOpen"
        width="40vw"
      >
        <component
          v-model="selectedItem"
          :hasUniqueName="hasUniqueName"
          :is="editDialogContent"
          :isEditable="isEditable"
          @cancel="handleCancel"
          @error="handleError"
          @success="handleSuccess"
        />
      </v-dialog>

      <DeleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="Site"
      />
    </template>
  </avs-card>
</template>

<script>
import { toDatetime } from "@/utils/datetime"
import { SpaceService } from "@/services"
import { mkUnique } from "@/utils/validator"

import DeleteDialog from "@/components/generics/dialogs/DeleteDialog.vue"
import SpaceEditor from "@/components/ri3/space/SpaceEditor.vue"

export default {
  name: "SpaceDetail",
  components: { DeleteDialog, SpaceEditor },
  created() {
    this.fetchFieldTypes()
  },
  computed: {
    hasUniqueName: {
      get() {
        const names = this.items.filter(e => e.name !== this.selectedItem?.name).map(e => e.name)
        return mkUnique(names, "name")
      }
    },
    project() {
      return this.$store.state.app.project
    },
    archivedFilterItems() {
      return [
        { text: this.$t("Actifs"), value: "active" },
        { text: this.$t("Archivés"), value: "archived" },
        { text: this.$t("commons.all_simple"), value: "all" }
      ]
    },
    filteredItems() {
      if (this.archivedFilter === "archived") return this.items.filter(i => i.archived)
      if (this.archivedFilter === "all") return this.items
      return this.items.filter(i => !i.archived)
    }
  },
  data() {
    return {
      headers: [
        {
          text: this.$t("commons.name"),
          value: "name",
          // class: "min-width-150",
          sortable: true
        },
        {
          text: this.$t("Prix"),
          value: "price",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("commons.created_at"),
          value: "created_at",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("commons.created_by"),
          value: "created_by",
          align: "center",
          sortable: true,
          filterable: false
        },

        {
          text: this.$t("commons.action"),
          value: "actions",
          align: "center",
          sortable: false,
          filterable: false
        }
      ],
      isEditable: true,
      items: [],
      search: "",
      archivedFilter: "active",

      // basecomponents: Object.values(COMPONENT_ENUM),
      deleteDialogIsOpen: false,
      editDialogIsOpen: false,
      editDialogContent: null,
      selectedItem: undefined,
      confirmDialogIsOpen: false
    }
  },
  methods: {
    toDatetime,
    fetchFieldTypes() {
      SpaceService.getAll(this, this.project.id)
        .then(response => {
          this.items = response
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
    },
    formatNumber(value) {
      if (!value) return "0"
      const rounded = Math.round(Number(value))
      return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    },
    isDisabled(item) {
      // Un espace déjà lié à des réservations ne peut être ni modifié ni supprimé
      // (le backend le refuse aussi) : on utilise l'archivage à la place.
      return !!item.is_used
    },
    handleOpenConfirmDialog(item) {
      this.selectedItem = item
      this.confirmDialogIsOpen = true
    },
    confirmAction(action) {
      this.confirmDialogIsOpen = false
      switch (action) {
        case "accept":
          this.authorizeRequest(this.selectedItem)
          break
        case "reject":
          this.rejectRequest(this.selectedItem)
          break
        case "close":
          this.send(this.$t("messages.info.operation_canceled"), "info")
          break
        default:
          break
      }
    },
    authorizeRequest(item) {
      SpaceService.authorize(item.id)
        .then(() => {
          this.handleSuccess()
        })
        .catch(error => {
          const message = error?.response.data?.[0] || this.$t("messages.error.operation_failed")
          this.handleError(message)
        })
    },
    rejectRequest(item) {
      SpaceService.reject(item.id)
        .then(() => {
          this.send(this.$t("messages.success.rejected"), "success")
          this.handleSuccess()
        })
        .catch(error => {
          const message = error?.response?.data?.[0] || this.$t("messages.error.operation_failed")
          this.handleError(message)
        })
    },
    handleStatusComponent(action) {
      return action.status
    },
    handleOpenDeleteDialog(item) {
      this.selectedItem = item
      this.deleteDialogIsOpen = true
    },
    handleArchive(item) {
      const archived = !item.archived
      SpaceService.archive(item.id, archived)
        .then(() => {
          this.send(archived ? this.$t("Espace archivé") : this.$t("Espace désarchivé"), "success")
          this.fetchFieldTypes()
        })
        .catch(error => {
          const message = error?.response?.data?.[0] || this.$t("messages.error.operation_failed")
          this.handleError(message)
        })
    },
    handleOpenEditDialog(item, show = true) {
      this.selectedItem = undefined
      this.isEditable = true
      if (item) {
        this.selectedItem = item
        if (show) {
          this.isEditable = false
        }
      }
      this.handleOpenEditDialogByComponent()
    },
    handleOpenEditDialogByComponent() {
      this.editDialogIsOpen = true
      this.editDialogContent = "SpaceEditor"
    },
    handleCancel() {
      this.selectedItem = undefined
      this.deleteDialogIsOpen = false
      this.editDialogIsOpen = false
      this.editDialogContent = null
    },
    handleError(message) {
      this.send(message || "Enter a valid email address.", "error")
    },
    handleSuccess() {
      this.handleCancel()
      this.$nextTick(() => {
        this.fetchFieldTypes()
        this.send(this.$t("messages.success.operation"), "success")
      })
    },
    handleDelete() {
      SpaceService.delete(this.selectedItem.id)
        .then(() => {
          this.handleSuccess()
        })
        .catch(error => {
          const message = error?.response.data?.[0]
          this.handleError(message)
        })
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    }
  }
}
</script>
